import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { zoomIn, zoomOut, resetZoom } from '../../utils/appZoom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import api from '../../api/api';

const MODULE_TABS = [
  { label: 'Cockpit',    path: '/' },
  { label: 'MRP',        path: '/mrp/wizard' },
  { label: 'Inventory',  path: '/inventory/items' },
  { label: 'Purchasing', path: '/purchasing/orders' },
  { label: 'Production', path: '/production/orders' },
];

const MENU_ITEMS = [
  // New, Open, Save and Save As were listed here and did nothing: they act on whatever
  // document is open, and no page tells this bar what that would mean. Every screen
  // already carries its own Save and Add buttons, so rather than leave four items that
  // look live and are not, the menu offers only what it can actually carry out.
  {
    label: 'File',
    items: [
      { label: 'Close', shortcut: 'Esc', action: () => window.history.back() },
    ],
  },
  // Add Row and Delete Row belong to a document's line grid, and Replace has no
  // meaning outside one. Find does: every list screen has a search box, and this puts
  // the cursor in it.
  {
    label: 'Edit',
    items: [
      {
        label: 'Find',
        shortcut: 'Ctrl+F',
        action: () => {
          const box = document.querySelector('.search-input, input[type="search"]');
          if (box) { box.focus(); box.select(); }
        },
      },
    ],
  },
  {
    label: 'View',
    items: [
      { label: 'Refresh', shortcut: 'F5', action: () => window.location.reload() },
      { divider: true },
      { label: 'Zoom In', action: zoomIn },
      { label: 'Zoom Out', action: zoomOut },
      { label: 'Reset Zoom', action: resetZoom },
    ],
  },
  {
    label: 'Modules',
    items: [
      ...MODULE_TABS.map((t) => ({ label: t.label, path: t.path })),
      { divider: true },
      // Belongs with the modules rather than Settings: anyone who edits data in SAP
      // may need to confirm it arrived, not just whoever administers the integration.
      { label: 'Check a SAP Update', path: '/sap-update-check' },
    ],
  },
  {
    label: 'Settings',
    items: [
      { label: 'Company Settings', path: '/admin/calendar' },
      { label: 'Email Notifications', path: '/admin/email-settings' },
      { label: 'SAP Integration', path: '/admin/sap-integration' },
      { label: 'SAP Live Updates', path: '/admin/sap-live-updates' },
      { label: 'Numbering', path: '/admin/numbering' },
      { divider: true },
      { label: 'System Information', action: 'system-info' },
    ],
  },
];

function ThemeToggleButton() {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <button
      type="button"
      title={isDark ? 'Switch to day view' : 'Switch to night view'}
      onClick={toggleTheme}
      className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors
                 text-sap-textMuted hover:text-sap-text dark:text-slate-400 dark:hover:text-slate-100"
      aria-label={isDark ? 'Switch to day view' : 'Switch to night view'}
    >
      {isDark ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
        </svg>
      )}
    </button>
  );
}

export default function TopMenuBar() {
  const { dispatch } = useApp();
  const { user, logout, idleMinutes } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  // Vendor delivery notices are worked out fresh on every request and there are
  // hundreds of them, so they drown everything else in a single list. The panel
  // offers them apart instead: everything, what the system recorded, or vendors.
  const [notifTab, setNotifTab] = useState('all');
  const [unreadCount, setUnreadCount] = useState(0);
  const [expandedNotif, setExpandedNotif] = useState(null);
  const [sysInfoOpen, setSysInfoOpen] = useState(false);
  const menuRef = useRef(null);
  const notifRef = useRef(null);

  const fetchNotifCount = useCallback(() => {
    api.get('/notifications/count').then(d => setUnreadCount(d.count || 0)).catch(() => {});
  }, []);

  const fetchNotifications = useCallback(() => {
    const source = notifTab === 'all' ? '' : `&source=${notifTab}`;
    api.get(`/notifications?limit=80${source}`)
      .then(d => setNotifications(Array.isArray(d) ? d : [])).catch(() => {});
  }, [notifTab]);

  useEffect(() => {
    fetchNotifCount();
    const interval = setInterval(fetchNotifCount, 60000);
    window.addEventListener('mrp:notifications-changed', fetchNotifCount);
    return () => {
      clearInterval(interval);
      window.removeEventListener('mrp:notifications-changed', fetchNotifCount);
    };
  }, [fetchNotifCount]);

  // Switching tab asks the server for that kind, so the list is a full page of it
  // rather than whatever survived being mixed with the others.
  useEffect(() => {
    if (notifOpen) fetchNotifications();
  }, [notifTab, notifOpen, fetchNotifications]);

  const handleBellClick = () => {
    if (!notifOpen) fetchNotifications();
    setNotifOpen(!notifOpen);
    setOpenMenu(null);
    setExpandedNotif(null);
  };

  const markAllRead = () => {
    api.post('/notifications/mark-read', {}).then(() => {
      fetchNotifCount();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      // The sidebar keeps its own copy of the count and would otherwise show the old
      // number until its next minute ticked over.
      window.dispatchEvent(new CustomEvent('mrp:notifications-changed'));
    }).catch(() => {});
  };

  const markOneRead = (id) => {
    api.post('/notifications/mark-read', { notification_id: id }).then(() => {
      setNotifications((prev) => prev.map((n) => (n.notification_id === id ? { ...n, is_read: 1 } : n)));
      fetchNotifCount();
      window.dispatchEvent(new CustomEvent('mrp:notifications-changed'));
    }).catch(() => {});
  };

  useEffect(() => {
    function handler(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleMenuClick = (label) => {
    setOpenMenu(openMenu === label ? null : label);
  };

  const handleMenuItemClick = (item) => {
    setOpenMenu(null);
    if (item.action === 'system-info') setSysInfoOpen(true);
    else if (typeof item.action === 'function') item.action();
    else if (item.path) navigate(item.path);
  };

  const menuItems = useMemo(() => MENU_ITEMS.map((menu) => {
    let items = menu.items;
    if (menu.label === 'Settings') {
      items = items.filter(item => {
        if (item.label === 'Numbering') {
          return user?.role_name === 'Super Admin';
        }
        return true;
      });
    }

    const baseMenu = {
      ...menu,
      items
    };

    if (menu.label !== 'View') return baseMenu;

    return {
      ...baseMenu,
      items: [
        baseMenu.items[0],
        { divider: true },
        {
          label: theme === 'dark' ? 'Day View' : 'Night View',
          action: toggleTheme,
        },
        ...baseMenu.items.slice(1),
      ],
    };
  }), [theme, toggleTheme, user]);

  const isTabActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const pageTitle = (() => {
    const tab = MODULE_TABS.find(t => isTabActive(t.path));
    if (tab) return tab.label;
    if (location.pathname.startsWith('/sales')) return 'Sales';
    if (location.pathname.startsWith('/admin')) return 'Administration';
    if (location.pathname.startsWith('/financials')) return 'Financials';
    if (location.pathname.startsWith('/guide')) return 'User Guide';
    return 'Agentfloww';
  })();

  /**
   * The File / Edit / View / Modules / Settings menus.
   *
   * Rendered in two places: inline in the header on a desktop, and on its own row
   * below the title on a phone, where there is no space beside a title and the
   * notification and account controls. It is one piece of markup used twice rather
   * than a second mobile menu, because a copy drifts out of step with the original.
   *
   * The mobile row wraps rather than scrolls on purpose: the five labels fit across a
   * 375px screen, and a horizontal scroller would clip the dropdowns, which hang below
   * their button.
   */
  const renderMenus = () => (
    <>
          {menuItems.map((menu) => (
            <div key={menu.label} className="relative">
              <button
                onClick={() => handleMenuClick(menu.label)}
                className={`px-2.5 py-1 text-xs rounded-lg transition-colors
                  ${openMenu === menu.label
                    ? 'bg-gray-100 dark:bg-slate-800 text-sap-text dark:text-slate-100 font-medium'
                    : 'text-sap-textLight dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-sap-text dark:hover:text-slate-100'}`}
              >
                {menu.label}
              </button>

              {openMenu === menu.label && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-slate-900 border border-sap-border dark:border-slate-700
                                shadow-lg rounded-xl py-1 z-50">
                  {menu.items.map((item, idx) =>
                    item.divider ? (
                      <hr key={idx} className="my-1 border-sap-border" />
                    ) : (
                      <button
                        key={item.label}
                        onClick={() => handleMenuItemClick(item)}
                        className="w-full text-left px-3 py-2 text-xs text-sap-text dark:text-slate-200
                                   hover:bg-sap-blueLight dark:hover:bg-slate-800 hover:text-sap-blue flex items-center justify-between"
                      >
                        <span>{item.label}</span>
                        {item.shortcut && (
                          <span className="text-sap-textMuted text-2xs ml-4">{item.shortcut}</span>
                        )}
                      </button>
                    )
                  )}
                </div>
              )}
            </div>
          ))}
    </>
  );

  return (
    <header className="shrink-0 bg-white dark:bg-slate-900 border-b border-sap-border dark:border-slate-700 transition-colors" ref={menuRef}>
      {/* Main header row */}
      <div className="h-14 flex items-center px-3 sm:px-6 select-none">
        <button
          type="button"
          onClick={() => dispatch({ type: 'OPEN_MOBILE_NAV' })}
          aria-label="Open navigation"
          className="lg:hidden -ml-1 mr-2 p-2 rounded-lg text-sap-textMuted hover:bg-gray-100
                     dark:text-slate-400 dark:hover:bg-slate-800 transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" aria-hidden>
            <path d="M3 6h18M3 12h18M3 18h18" />
          </svg>
        </button>
        {/* The title gives up its width first: on a narrow screen the controls on the
            right are what people came for, and a clipped heading still identifies the
            page. */}
        <h1 className="text-base sm:text-lg font-bold text-sap-text dark:text-slate-100 tracking-tight
                       mr-3 sm:mr-6 truncate min-w-0">{pageTitle}</h1>

        {/* Menus — inline beside the title on a desktop. */}
        <nav className="hidden lg:flex items-center relative z-50">
          {renderMenus()}
        </nav>

        <div className="flex-1" />

        {/* Right actions */}
        <div className="flex items-center gap-1 sm:gap-2">
          <button title="Refresh" onClick={() => window.location.reload()}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors text-sap-textMuted hover:text-sap-text dark:text-slate-400 dark:hover:text-slate-100">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 4v6h-6M1 20v-6h6" /><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
            </svg>
          </button>

          <ThemeToggleButton />

          <button title="Toggle Sidebar" onClick={() => dispatch({ type: 'TOGGLE_SIDEBAR' })}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors text-sap-textMuted hover:text-sap-text dark:text-slate-400 dark:hover:text-slate-100">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="18" rx="1" /><rect x="14" y="3" width="7" height="18" rx="1" />
            </svg>
          </button>

          {/* Notification Bell */}
          <div className="relative" ref={notifRef}>
            <button title="Notifications" onClick={handleBellClick}
              className="relative p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors text-sap-textMuted hover:text-sap-text dark:text-slate-400 dark:hover:text-slate-100">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 flex items-center justify-center
                                 bg-red-500 text-white text-[9px] font-bold rounded-full leading-none">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <div className="absolute right-0 top-full mt-2 w-96 bg-white dark:bg-slate-900 border border-sap-border dark:border-slate-700
                              shadow-xl rounded-2xl z-50 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-sap-border dark:border-slate-700 bg-gray-50 dark:bg-slate-800/80">
                  <div className="min-w-0">
                    <span className="text-sap-text text-sm font-semibold block">Notifications</span>
                    <span className="text-sap-textMuted text-[10px]">Latest first</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <button onClick={markAllRead}
                        className="text-sap-blue text-[10px] hover:underline transition-colors">
                        Mark all read
                      </button>
                    )}
                    <button onClick={() => { api.post('/notifications/generate').then(() => { fetchNotifications(); fetchNotifCount(); }).catch(() => {}); }}
                      className="text-sap-textMuted text-[10px] hover:text-sap-text transition-colors">
                      Refresh
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1 px-3 py-2 border-b border-sap-border dark:border-slate-700">
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'production', label: 'Production' },
                    { key: 'purchase', label: 'Purchase orders' },
                    { key: 'vendor', label: 'Vendors' },
                  ].map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setNotifTab(t.key)}
                      className={`px-2.5 py-1 text-[11px] font-semibold rounded-full transition-colors
                        ${notifTab === t.key
                          ? 'bg-sap-blue text-white'
                          : 'text-sap-textMuted hover:bg-gray-100 dark:hover:bg-slate-800'}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                <div className="max-h-80 overflow-y-auto divide-y divide-sap-border/50 dark:divide-slate-700">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sap-textMuted dark:text-slate-500 text-xs">
                      {notifTab === 'vendor'
                        ? 'No vendor notifications'
                        : notifTab === 'production'
                          ? 'No production notifications'
                          : notifTab === 'purchase'
                            ? 'No purchase order notifications'
                            : 'No notifications'}
                    </div>
                  ) : notifications.map((n, idx) => {
                    const isExpanded = expandedNotif === n.notification_id;
                    const pcode = (n.project_code && String(n.project_code).trim()) || '';
                    const pname = (n.project_name && String(n.project_name).trim()) || '';
                    return (
                    <div key={n.notification_id}
                      onClick={() => {
                        if (!n.is_read) markOneRead(n.notification_id);
                        setExpandedNotif(isExpanded ? null : n.notification_id);
                      }}
                      className={`px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors
                        ${!n.is_read ? 'bg-sap-blueLight/50 dark:bg-sap-blue/10' : ''}`}>
                      <div className="flex items-start gap-2">
                        <NotifIcon severity={n.severity} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs font-semibold text-sap-text dark:text-slate-200 truncate">{n.title}</span>
                            {!n.is_read && <span className="w-1.5 h-1.5 rounded-full bg-sap-blue shrink-0" />}
                            {idx === 0 && (
                              <span className="text-[8px] font-bold uppercase text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Latest</span>
                            )}
                          </div>
                          <div className="text-[10px] text-sap-textLight mt-1 space-y-0.5">
                            <div className="truncate">
                              <span className="text-sap-textMuted">Project code: </span>
                              <span className="font-semibold">{pcode || '—'}</span>
                            </div>
                            <div className="truncate" title={pname || undefined}>
                              <span className="text-sap-textMuted">Project name: </span>
                              <span className="font-semibold">{pname || '—'}</span>
                            </div>
                          </div>
                          <p className={`text-[10px] text-sap-textMuted mt-0.5 ${isExpanded ? '' : 'line-clamp-2'}`}>
                            {String(n.message || '')}
                          </p>
                          {isExpanded && n.related_entity === 'production_orders' && n.related_id && (
                            <button
                              type="button"
                              className="text-[10px] text-sap-blue mt-1 hover:underline"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/production-order/${n.related_id}`);
                                setNotifOpen(false);
                              }}
                            >
                              Open production order
                            </button>
                          )}
                          <span className="text-[9px] text-sap-textMuted mt-1 block">
                            {new Date(n.created_at).toLocaleString()}
                          </span>
                        </div>
                        <svg className={`w-3 h-3 text-sap-textMuted mt-1 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
                      </div>
                    </div>
                  );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* User avatar + role */}
          <div className="flex items-center gap-2 pl-2 border-l border-sap-border dark:border-slate-700">
            <div className="w-8 h-8 rounded-full bg-sap-blueLight dark:bg-sap-blue/20 ring-2 ring-sap-blue/20 flex items-center justify-center text-sap-blue text-xs font-bold" title={user?.name || ''}>
              {user?.name ? user.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : 'U'}
            </div>
            <span className="text-xs text-sap-textLight dark:text-slate-400 hidden sm:block max-w-[100px] truncate">{user?.role_name}</span>
          </div>

          <button title="Log out" onClick={logout}
            className="p-2 rounded-xl hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors text-sap-textMuted dark:text-slate-400 hover:text-red-500">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </div>

      {sysInfoOpen && (
        <SystemInfoPanel user={user} idleMinutes={idleMinutes} onClose={() => setSysInfoOpen(false)} />
      )}

      {/* The same menus on a phone, where they do not fit beside the title. */}
      <nav className="lg:hidden flex flex-wrap items-center gap-0.5 px-2 pb-1.5 relative z-50">
        {renderMenus()}
      </nav>

      {/* Module tabs */}
      <div className="h-10 flex items-center px-3 sm:px-6 select-none overflow-x-auto no-scrollbar border-t border-sap-border/50 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/50">
        {MODULE_TABS.map((tab) => {
          const active = isTabActive(tab.path);
          return (
            <button
              key={tab.label}
              onClick={() => navigate(tab.path)}
              className={`relative px-3 sm:px-4 h-full text-sm whitespace-nowrap shrink-0 transition-colors
                ${active
                  ? 'text-sap-blue font-semibold'
                  : 'text-sap-textLight dark:text-slate-400 hover:text-sap-text dark:hover:text-slate-100'}`}
            >
              {tab.label}
              {active && (
                <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-sap-blue rounded-full" />
              )}
            </button>
          );
        })}
      </div>
    </header>
  );
}

function NotifIcon({ severity }) {
  const colors = {
    danger:  'text-red-500',
    warning: 'text-amber-500',
    success: 'text-emerald-500',
    info:    'text-sap-blue',
  };
  const icons = {
    danger:  <path d="M12 9v4m0 4h.01M4.93 19h14.14c1.1 0 1.79-1.19 1.24-2.14l-7.07-12.24c-.55-.95-1.92-.95-2.47 0L3.69 16.86C3.14 17.81 3.83 19 4.93 19z" />,
    warning: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>,
    success: <><path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>,
    info:    <><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></>,
  };
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      className={`shrink-0 mt-0.5 ${colors[severity] || colors.info}`}>
      {icons[severity] || icons.info}
    </svg>
  );
}

/**
 * What build am I on, and who am I signed in as?
 *
 * The first question support asks is which bundle the person is running, and until now
 * there was no way to answer it from the screen - the filename changes with every
 * build and lives only in the page source. The rest is the session detail that saves a
 * round of "try logging out and in again".
 */
function SystemInfoPanel({ user, idleMinutes, onClose }) {
  const bundle = (() => {
    const el = document.querySelector('script[src*="/static/js/main."]');
    const m = el && el.getAttribute('src').match(/main\.[a-z0-9]+\.js/i);
    return m ? m[0] : 'unknown';
  })();

  const rows = [
    ['Signed in as', user?.name || '—'],
    ['Email', user?.email || '—'],
    ['Role', user?.role_name || '—'],
    ['Signed out after', `${idleMinutes || 30} minutes idle`],
    ['Build', bundle],
    ['Address', window.location.origin],
    ['Screen', `${window.innerWidth} × ${window.innerHeight}`],
    ['Browser time', new Date().toString().replace(/\s*\(.*\)$/, '')],
  ];

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <button type="button" aria-label="Close" className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-xl shadow-2xl
                      border border-sap-border dark:border-slate-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-sap-border dark:border-slate-700 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-sap-text dark:text-slate-100">System information</h2>
          <button type="button" onClick={onClose}
                  className="text-gray-400 hover:text-gray-700 dark:hover:text-slate-200 text-xl leading-none px-1">
            &times;
          </button>
        </div>
        <dl className="p-4 space-y-2">
          {rows.map(([k, v]) => (
            <div key={k} className="flex gap-3 text-xs">
              <dt className="w-32 shrink-0 text-sap-textLight dark:text-slate-400">{k}</dt>
              <dd className="font-mono text-sap-text dark:text-slate-200 break-all">{v}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
